// 周期性任务工具：从一个 base date 计算下一次发生日期、格式化标签、预设。
// 设计原则：所有计算都用本地日期 (YYYY-MM-DD)；不卷入时区。
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
  format,
  parseISO,
} from 'date-fns';
import type { Recurrence } from './types';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isValidRecurrence(r: unknown): r is Recurrence {
  if (!r || typeof r !== 'object') return false;
  const v = r as Recurrence;
  if (!['daily', 'weekly', 'monthly', 'yearly'].includes(v.freq)) return false;
  if (!Number.isInteger(v.interval) || v.interval < 1 || v.interval > 365) return false;
  if (v.byWeekday) {
    if (!Array.isArray(v.byWeekday)) return false;
    // 与 @doneline/shared recurrenceSchema 一致：byWeekday 最多 7 个、元素 0-6。漏了 max(7) 会让
    // 导入一份 8+ 元素的备份在客户端判为合法、却在 push 时被服务端 recurrenceSchema 拒（整条 task
    // op 被 reject → 静默丢这条任务）。对齐长度上限，非法则降级 null（import）/ 不渲染。
    if (v.byWeekday.length > 7) return false;
    if (v.byWeekday.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return false;
  }
  if (v.endDate != null && !ISO_DATE_RE.test(v.endDate)) return false;
  return true;
}

// 给定 base 日期 (YYYY-MM-DD) 和 recurrence，返回下一次发生的日期。
// 若超出 endDate 则返回 null（调用方据此关闭 task）。
// weekly + byWeekday: 返回下一个匹配的工作日（在当前 interval 周内或下个 interval）。
export function computeNextDate(baseDate: string, rec: Recurrence): string | null {
  const base = parseISO(baseDate);
  // 损坏的 base（如同步回来的非法 dueDate）→ 返回 null 当作"无下次"，避免后面 format(Invalid)
  // 抛 RangeError 把 toggleTaskStatus（完成勾选）整条卡死。
  if (Number.isNaN(base.getTime())) return null;
  let next: Date;

  if (rec.freq === 'daily') {
    next = addDays(base, rec.interval);
  } else if (rec.freq === 'weekly') {
    if (rec.byWeekday && rec.byWeekday.length > 0) {
      const sorted = [...rec.byWeekday].sort((a, b) => a - b);
      const baseDow = base.getDay();
      // 在当前周内尝试找一个比 base 更晚的指定 weekday
      const laterToday = sorted.find((d) => d > baseDow);
      // base 本身是一次发生（advanceTaskDate 从当前 dueDate 推进），故 base 所在周是"on"周：
      // 只要本周还有更晚的指定 weekday 就推进到它，与 interval 无关。
      // 修复前 interval>1 时无视本周剩余 weekday 直接跳 interval 周 → 漏掉同周其余日
      // （如"每两周 一/三/五"完成周一后会跳到两周后的周一，吞掉本周三、周五）。
      if (laterToday !== undefined) {
        next = addDays(base, laterToday - baseDow);
      } else {
        // 本周已无更晚的指定日 → 跳到 interval 周后的最早指定 weekday
        const weeksAhead = rec.interval;
        const targetDow = sorted[0]!;
        const daysToAdvance = weeksAhead * 7 - baseDow + targetDow;
        next = addDays(base, daysToAdvance);
      }
    } else {
      next = addDays(base, rec.interval * 7);
    }
  } else if (rec.freq === 'monthly') {
    next = addMonths(base, rec.interval);
  } else {
    next = addYears(base, rec.interval);
  }

  const nextStr = format(next, 'yyyy-MM-dd');
  if (rec.endDate && nextStr > rec.endDate) return null;
  return nextStr;
}

// 判断一个"无显式日期、仅靠 recurrence 漂浮"的周期任务是否在某个目标日期 (YYYY-MM-DD) 发生。
// 用于 今日 / 未来 视图把这类任务投射到具体日期上显示（如"每周二"在每个周二出现）。
// anchor（默认调用方传今天）提供周期相位基准，且不回溯 anchor 之前的日期。
export function recurrenceOccursOn(rec: Recurrence, dateStr: string, anchorStr: string): boolean {
  if (rec.endDate && dateStr > rec.endDate) return false;
  if (dateStr < anchorStr) return false; // 漂浮任务只向未来投射，不回溯
  const date = parseISO(dateStr);
  const anchor = parseISO(anchorStr);

  switch (rec.freq) {
    case 'daily':
      return differenceInCalendarDays(date, anchor) % rec.interval === 0;
    case 'weekly':
      if (rec.byWeekday && rec.byWeekday.length > 0) {
        if (!rec.byWeekday.includes(date.getDay())) return false;
        if (rec.interval === 1) return true;
        // interval>1：以 anchor 所在自然周为相位基准
        return differenceInCalendarWeeks(date, anchor, { weekStartsOn: 0 }) % rec.interval === 0;
      }
      // 无 byWeekday：以 anchor 当天的星期为准，每 interval 周一次
      return differenceInCalendarDays(date, anchor) % (rec.interval * 7) === 0;
    case 'monthly':
      if (date.getDate() !== anchor.getDate()) return false;
      return differenceInCalendarMonths(date, anchor) % rec.interval === 0;
    default: // yearly
      if (date.getMonth() !== anchor.getMonth() || date.getDate() !== anchor.getDate())
        return false;
      return differenceInCalendarYears(date, anchor) % rec.interval === 0;
  }
}

// 漂浮重复任务从 anchor（调用方传今天）起的首个发生日，与 recurrenceOccursOn 的投射
// 语义同源：daily / weekly无byWeekday / monthly / yearly 以 anchor 为相位基准，首个发生
// 就是 anchor 当天；只有 weekly+byWeekday 可能落在未来的某个周几（anchor 所在周即 on 周，
// interval*7+7 天内必有一次发生）。endDate 已截止则 null。
export function firstOccurrenceOnOrAfter(rec: Recurrence, anchorStr: string): string | null {
  if (rec.endDate && anchorStr > rec.endDate) return null;
  if (rec.freq !== 'weekly' || !rec.byWeekday || rec.byWeekday.length === 0) return anchorStr;
  const anchor = parseISO(anchorStr);
  if (Number.isNaN(anchor.getTime())) return null;
  for (let i = 0; i <= rec.interval * 7 + 7; i++) {
    const d = format(addDays(anchor, i), 'yyyy-MM-dd');
    if (recurrenceOccursOn(rec, d, anchorStr)) return d;
  }
  return null;
}

// 逾期循环任务快进：从旧 dueDate 推进一步可能仍落在过去（如「每周一」逾期两周，完成后
// 推到上周一 → 立即又显示逾期，看起来"完成了没反应"）。被错过的中间发生不逐次补收，
// 直接快进到首个 ≥ 今天的发生日；快进途中撞 endDate 则 null（调用方按 exhausted 处理）。
// 迭代护栏：格式合法但久远的日期（如导入的 dueDate='0001-01-01'，import 清洗只校验格式
// 不校验范围）会让逐次推进跑 ~74 万轮 parseISO+addDays+format，同步冻结主线程数十秒。
// 超限即放弃不可考的历史相位，直接以今天为相位基准重锚（firstOccurrenceOnOrAfter：
// daily / weekly无byWeekday / monthly / yearly 就是今天本身，weekly+byWeekday 是今天起
// 首个匹配周几；自带 endDate 检查，exhausted 语义不破）——可接受的降级，绝不返回过去的日期。
const FAST_FORWARD_MAX_STEPS = 1000;

function fastForwardPast(next: string | null, rec: Recurrence, todayStr: string): string | null {
  let steps = 0;
  while (next && next < todayStr) {
    if (++steps > FAST_FORWARD_MAX_STEPS) return firstOccurrenceOnOrAfter(rec, todayStr);
    next = computeNextDate(next, rec);
  }
  return next;
}

// 推进 task 的 dueDate 到下一次发生。
// 优先以 dueDate 为基准；若 task 只有 dueAt，则以 dueAt 的本地日期推进 dueAt 时间。
// start_date 区间任务暂不支持周期性（UI 也不暴露）。
export function advanceTaskDate(
  task: {
    dueDate: string | null;
    dueAt: string | null;
  },
  rec: Recurrence,
): {
  dueDate: string | null;
  dueAt: string | null;
  exhausted: boolean;
} {
  const today = format(new Date(), 'yyyy-MM-dd');
  if (task.dueDate) {
    const next = fastForwardPast(computeNextDate(task.dueDate, rec), rec, today);
    if (!next) return { dueDate: null, dueAt: null, exhausted: true };
    return { dueDate: next, dueAt: null, exhausted: false };
  }
  if (task.dueAt) {
    const orig = parseISO(task.dueAt);
    // 损坏 dueAt：退回"以今天为基准"，避免 format(Invalid) 抛 RangeError 卡死完成操作。
    if (Number.isNaN(orig.getTime())) {
      const next = computeNextDate(today, rec);
      if (!next) return { dueDate: null, dueAt: null, exhausted: true };
      return { dueDate: next, dueAt: null, exhausted: false };
    }
    // 基准必须用 UTC-slice（dueAt.slice(0,10)），与全 app 对 dueAt 的归桶口径一致
    // （taskCoversToday/upcomingDay/calendar/reschedule 全按 UTC 日）。曾用 format(orig)
    // 取**本地日**：进程时区≠UTC 时 base 与归桶差 ±1 天 → 完成后 UTC-slice 前进 interval±1
    // （Asia/Shanghai 早晨每日任务隔天跳、美洲晚间任务卡在原地）。CI 跑 UTC 测不出，
    // 现由 vitest 钉 TZ=Asia/Shanghai + 跨午夜用例守护。
    const base = task.dueAt.slice(0, 10);
    const next = fastForwardPast(computeNextDate(base, rec), rec, today);
    if (!next) return { dueDate: null, dueAt: null, exhausted: true };
    // 保留原 HH:mm:ss（UTC 分量），仅推进 UTC 日历日
    const nextDt = parseISO(`${next}T00:00:00.000Z`);
    nextDt.setUTCHours(orig.getUTCHours(), orig.getUTCMinutes(), orig.getUTCSeconds(), 0);
    return { dueDate: null, dueAt: nextDt.toISOString(), exhausted: false };
  }
  // 漂浮重复任务（无任何日期）：基准是「视图里正被完成的那次发生」= 首个 ≥ 今天的发生日，
  // 而不是今天本身。今日/未来视图按 recurrenceOccursOn(anchor=今天) 把这类任务投射到未来
  // 的周几（如「每周三」显示在明天）；曾直接用今天作基准 → computeNextDate(周二, 每周三)
  // 又算回同一个周三，完成后任务原地留在「明天」桶不推进。
  const occurrence = firstOccurrenceOnOrAfter(rec, today);
  if (!occurrence) return { dueDate: null, dueAt: null, exhausted: true };
  const next = computeNextDate(occurrence, rec);
  if (!next) return { dueDate: null, dueAt: null, exhausted: true };
  return { dueDate: next, dueAt: null, exhausted: false };
}

// 自定义重复编辑器的草稿 → 规范化的 Recurrence（与 apps/mobile/lib/recurrence.ts 1:1 对齐）：
// - interval Math.trunc 后夹到 1..365 —— number input 可输入/粘贴 '2.5'，非整数 interval 落库
//   会被服务端 recurrenceSchema 的 int 校验拒（整条 op 丢弃 = recurrence 静默不上行），且
//   isValidRecurrence 判 false → 完成勾选走普通分支、周期任务不推进
// - 仅 weekly 且选了周几时保留并排序 byWeekday；否则丢弃
// - endDate 为空串 / null 时省略
// 返回的对象应再过 isValidRecurrence 校验后才落库。
export function normalizeCustomRecurrence(draft: Recurrence): Recurrence {
  const interval = Math.min(365, Math.max(1, Math.trunc(draft.interval)));
  const out: Recurrence = { freq: draft.freq, interval };
  if (draft.freq === 'weekly' && draft.byWeekday && draft.byWeekday.length > 0) {
    out.byWeekday = [...draft.byWeekday].sort((a, b) => a - b);
  }
  if (draft.endDate) out.endDate = draft.endDate;
  return out;
}

export const RECURRENCE_PRESETS: { id: string; label: string; value: Recurrence }[] = [
  { id: 'daily', label: 'recurrence.preset.daily', value: { freq: 'daily', interval: 1 } },
  {
    id: 'weekdays',
    label: 'recurrence.preset.weekdays',
    value: { freq: 'weekly', interval: 1, byWeekday: [1, 2, 3, 4, 5] },
  },
  { id: 'weekly', label: 'recurrence.preset.weekly', value: { freq: 'weekly', interval: 1 } },
  { id: 'biweekly', label: 'recurrence.preset.biweekly', value: { freq: 'weekly', interval: 2 } },
  { id: 'monthly', label: 'recurrence.preset.monthly', value: { freq: 'monthly', interval: 1 } },
  { id: 'yearly', label: 'recurrence.preset.yearly', value: { freq: 'yearly', interval: 1 } },
];

const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

// 把 recurrence 渲染成给 UI 用的可读 i18n 路径 + 参数。
// 调用方拿到后用 t(key, params) 渲染。
export function describeRecurrence(rec: Recurrence): {
  key: string;
  params?: Record<string, string | number>;
} {
  if (rec.freq === 'daily') {
    return rec.interval === 1
      ? { key: 'recurrence.summary.daily' }
      : { key: 'recurrence.summary.everyNDays', params: { n: rec.interval } };
  }
  if (rec.freq === 'weekly') {
    if (rec.byWeekday && rec.byWeekday.length > 0) {
      const days = [...rec.byWeekday]
        .sort((a, b) => a - b)
        .map((d) => DOW_KEYS[d])
        .join(',');
      return rec.interval === 1
        ? { key: 'recurrence.summary.weeklyOn', params: { days } }
        : { key: 'recurrence.summary.everyNWeeksOn', params: { n: rec.interval, days } };
    }
    return rec.interval === 1
      ? { key: 'recurrence.summary.weekly' }
      : { key: 'recurrence.summary.everyNWeeks', params: { n: rec.interval } };
  }
  if (rec.freq === 'monthly') {
    return rec.interval === 1
      ? { key: 'recurrence.summary.monthly' }
      : { key: 'recurrence.summary.everyNMonths', params: { n: rec.interval } };
  }
  return rec.interval === 1
    ? { key: 'recurrence.summary.yearly' }
    : { key: 'recurrence.summary.everyNYears', params: { n: rec.interval } };
}

// 把 recurrence 渲染成本地化的人类可读字符串（如 "每周二"）。
// 给 UI 直接展示用；内部已处理 weekday 列表的翻译与拼接。
export function formatRecurrence(
  rec: Recurrence,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const desc = describeRecurrence(rec);
  if (!desc.params) return t(desc.key);
  const params = { ...desc.params };
  if (typeof params.days === 'string') {
    params.days = params.days
      .split(',')
      .map((k) => t(`recurrence.weekday.${k}`))
      .join('·');
  }
  return t(desc.key, params);
}
